/**
 * LyricsOverlay — timestamped line-by-line sync overlay for the player.
 *
 * Flat mode:   the entire word-row glides via CSS translateX so the active
 *              word drifts to centre in one continuous, ultra-slow motion —
 *              no jumps, no bursts.  A single long transition runs from the
 *              moment the first word activates all the way to the last.
 *              Opacity + blur decay by distance give the smoky dissipation.
 *
 * Grouped mode: section-aware rows, active section full-brightness, rest dimmed.
 *
 * Vocal glow: pulsing red text-shadow on active word when lyric density
 * implies vocal presence (gap to next ≤ VOCAL_GAP_THRESHOLD). Long gaps
 * (instrumentals) show no pulse.
 */
import { useEffect, useRef, useState, useMemo, useCallback } from "react";

/** Seconds after the last lyric before the overlay begins to fade out. */
const FADE_OUT_DELAY = 4.0;
/** Duration of the post-lyrics fade-out (matched in inline transition). */
const FADE_OUT_DUR   = "4.0s";

export interface LyricWord {
  timestamp: number;
  translation: string;
  break?: boolean;
  section?: string;
}

interface LineGroup {
  section?: string;
  words: Array<{ word: LyricWord; index: number }>;
}

interface Props {
  lyrics: LyricWord[] | null | undefined;
  lyricsStatus: string;
  currentTime: number;
  playing: boolean;
  trackId: string | null;
  /** Fallback title shown when no lyrics are available */
  trackTitle?: string;
}

/** Vocal-presence heuristic — short gap to next entry implies mid-phrase. */
const VOCAL_GAP_THRESHOLD = 1.5; // seconds

function isVocalWord(lyrics: LyricWord[], idx: number): boolean {
  if (idx < 0 || idx >= lyrics.length) return false;
  const next = lyrics[idx + 1];
  if (!next) return true;
  return next.timestamp - lyrics[idx].timestamp <= VOCAL_GAP_THRESHOLD;
}

function groupLines(lyrics: LyricWord[]): LineGroup[] {
  const lines: LineGroup[] = [];
  let current: LineGroup = { words: [] };
  lyrics.forEach((word, i) => {
    const startsNew = word.break || word.section != null;
    if (startsNew && current.words.length > 0) {
      lines.push(current);
      current = { section: word.section, words: [] };
    } else if (word.section != null && current.words.length === 0) {
      current.section = word.section;
    }
    current.words.push({ word, index: i });
  });
  if (current.words.length > 0) lines.push(current);
  return lines;
}

/** Opacity by distance from active word — all fully visible, no dissipation */
const distOpacity = [1, 1, 1, 1];
/** Blur (px) by distance — removed, all sharp */
const distBlur    = [0, 0, 0, 0];

export default function LyricsOverlay({
  lyrics,
  lyricsStatus,
  currentTime,
  playing,
  trackId,
  trackTitle,
}: Props) {
  const [activeIdx, setActiveIdx]     = useState(-1);
  const [visible, setVisible]         = useState(false);
  const [lyricsDone, setLyricsDone]   = useState(false);
  // translateX offset (px) applied to the flat-mode inner row
  const [slideX, setSlideX]           = useState(0);

  const activeRef    = useRef<HTMLSpanElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rowRef       = useRef<HTMLDivElement | null>(null);

  const reducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  // Reset + re-trigger entrance animation on track change
  useEffect(() => {
    setActiveIdx(-1);
    setSlideX(0);
    setLyricsDone(false);
    if (reducedMotion) { setVisible(true); return; }
    setVisible(false);
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [trackId, reducedMotion]);

  // Advance active index
  useEffect(() => {
    if (!lyrics || lyrics.length === 0) return;
    let idx = -1;
    for (let i = 0; i < lyrics.length; i++) {
      if (lyrics[i].timestamp <= currentTime) idx = i;
      else break;
    }
    setActiveIdx(idx);
  }, [currentTime, lyrics]);

  // Detect when all lyrics have been shown and schedule fade-out
  useEffect(() => {
    if (!lyrics || lyrics.length === 0) return;
    const lastTs = lyrics[lyrics.length - 1].timestamp;
    if (currentTime > lastTs + FADE_OUT_DELAY) {
      setLyricsDone(true);
    }
  }, [currentTime, lyrics]);

  // ── Continuous translateX scroll (flat mode only) ──────────────────────
  // Measures the active word's position relative to the container centre and
  // applies a single CSS transform.  The transition duration on the row is
  // set long enough that it spans several word-changes, so movement is one
  // unbroken, slow glide rather than discrete hops.
  const computeSlide = useCallback(() => {
    if (!activeRef.current || !containerRef.current || !rowRef.current) return;
    const container = containerRef.current;
    const word      = activeRef.current;
    const row       = rowRef.current;

    // Position of the active word relative to the row's left edge
    const wordLeft  = word.offsetLeft;
    const wordW     = word.offsetWidth;
    const containerW = container.offsetWidth;

    // We want the word's centre to sit at the container's centre
    const targetX = -(wordLeft + wordW / 2 - containerW / 2);
    setSlideX(targetX);
  }, []);

  useEffect(() => {
    // Use a tiny delay so the DOM has painted the new active span
    const id = requestAnimationFrame(computeSlide);
    return () => cancelAnimationFrame(id);
  }, [activeIdx, computeSlide]);

  // Re-measure on resize
  useEffect(() => {
    const ro = new ResizeObserver(computeSlide);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [computeSlide]);

  const hasLyrics    = lyricsStatus === "published" && Array.isArray(lyrics) && lyrics.length > 0;
  const showFallback = lyricsStatus === "pending" || (lyricsStatus === "published" && (!lyrics || lyrics.length === 0));

  const lines = useMemo<LineGroup[]>(() => {
    if (!hasLyrics) return [];
    return groupLines(lyrics!);
  }, [lyrics, hasLyrics]);

  const hasGroups = useMemo(() => {
    if (!lyrics) return false;
    return lyrics.some(w => w.break || w.section != null);
  }, [lyrics]);

  if (lyricsStatus === "none" && !hasLyrics && !trackTitle) return null;

  // Wrapper: always fully visible, no fade-out
  const wrapperOpacity   = 1;
  const wrapperTransform = "translateY(0)";
  const wrapperTransition = "none";

  return (
    <div
      className="w-full flex flex-col items-center"
      style={{
        opacity:    wrapperOpacity,
        transform:  wrapperTransform,
        transition: wrapperTransition,
      }}
    >
      {hasLyrics ? (
        hasGroups ? (
          /* ── Sectioned / grouped view ─────────────────────────────────── */
          <div className="flex flex-col items-center gap-3 w-full">
            {lines.map((line, li) => {
              const isActiveLine   = line.words.some(w => w.index === activeIdx);
              const activeLine     = lines.findIndex(l => l.words.some(w => w.index === activeIdx));
              const distFromActive = Math.abs(li - (activeLine < 0 ? 0 : activeLine));
              const isVisible      = distFromActive <= 2;

              return (
                <div
                  key={li}
                  className="w-full flex flex-col items-center gap-0.5"
                  style={{
                    transition:    "none",
                    opacity:       1,
                    maxHeight:     isVisible ? "8rem" : 0,
                    overflow:      "hidden",
                    pointerEvents: "none",
                  }}
                >
                  {line.section && (
                    <span className="font-sans text-[8px] uppercase tracking-[0.22em] text-absa-red/60 mb-0.5">
                      {line.section}
                    </span>
                  )}
                  <p
                    className="font-serif text-center leading-snug flex flex-wrap justify-center gap-x-2 gap-y-1.5"
                    style={{ fontSize: "1.05rem" }}
                    aria-label={line.words.map(w => w.word.translation).join(" ")}
                  >
                    {line.words.map(({ word, index: i }) => {
                      const isCurrent = i === activeIdx;
                      const isVocal   = isCurrent && playing && !reducedMotion && isVocalWord(lyrics!, i);
                      return (
                        <span
                          key={isCurrent ? `w-${i}-active` : i}
                          ref={isCurrent ? activeRef : null}
                          aria-hidden={!isCurrent}
                          className={[
                            isVocal ? "lyric-word-pulse" : "",
                            isCurrent && !reducedMotion ? "lyric-line-enter" : "",
                          ].filter(Boolean).join(" ") || undefined}
                          style={{
                            transition: "none",
                            opacity:    1,
                            color: isCurrent
                              ? "hsl(var(--foreground))"
                              : "hsl(var(--foreground) / 0.60)",
                            fontWeight: isCurrent ? 600 : 400,
                          }}
                        >
                          {word.translation}
                        </span>
                      );
                    })}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Feather-drift flat mode ───────────────────────────────────── *
           * The inner flex row is shifted via a single CSS translateX that   *
           * updates on every word change.  A 9s ease transition on the row   *
           * means successive small nudges merge into one unbroken, glacial   *
           * glide — no jumps, no restarts, just continuous drift.            *
           * ──────────────────────────────────────────────────────────────── */
          <div
            ref={containerRef}
            style={{
              overflow: "hidden",
              position: "relative",
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%)",
              maskImage:
                "linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%)",
              minHeight: 34,
              width: "100%",
            }}
          >
            <div className={reducedMotion ? undefined : "lyric-sway"}>
            <div
              ref={rowRef}
              className="flex items-baseline gap-x-3"
              style={{
                whiteSpace: "nowrap",
                willChange: "transform",
                // One long, unbroken transition — each word-change nudges the
                // target; the browser interpolates continuously, so the row
                // appears to glide without ever resetting or jumping.
                transition: reducedMotion ? "none" : "transform 12s cubic-bezier(0.25, 0.1, 0.25, 1)",
                transform: `translateX(${slideX}px)`,
                // left padding so the first word can reach centre
                paddingLeft: containerRef.current
                  ? `${containerRef.current.offsetWidth / 2}px`
                  : "50%",
                paddingRight: containerRef.current
                  ? `${containerRef.current.offsetWidth / 2}px`
                  : "50%",
              }}
            >
              {lyrics!.map((word, i) => {
                const dist      = Math.abs(i - Math.max(0, activeIdx));
                const inWindow  = dist <= 3;
                const isCurrent = i === activeIdx;
                const isVocal   = isCurrent && playing && !reducedMotion && isVocalWord(lyrics!, i);

                return (
                  <span
                    key={i}
                    ref={isCurrent ? activeRef : null}
                    aria-hidden={!isCurrent}
                    className={[
                      isVocal ? "lyric-word-pulse" : "",
                      isCurrent && !reducedMotion ? "lyric-line-enter" : "",
                    ].filter(Boolean).join(" ") || undefined}
                    style={{
                      display:    "inline-block",
                      whiteSpace: "nowrap",
                      opacity:    inWindow ? 1 : 0,
                      color: isCurrent
                        ? "hsl(var(--foreground))"
                        : "hsl(var(--foreground) / 0.70)",
                      fontWeight:  isCurrent ? 600 : 400,
                      fontSize:    "1.05rem",
                      fontFamily:  "var(--font-serif)",
                      transition:  "none",
                      willChange:  "transform",
                    }}
                  >
                    {word.translation}
                  </span>
                );
              })}
            </div>
            </div>
          </div>
        )
      ) : showFallback ? (
        <p className="font-sans text-[11px] text-center text-muted-foreground/40 italic py-1">
          {trackTitle ?? "Lyrics coming soon"}
        </p>
      ) : (lyricsStatus === "none" && trackTitle) ? (
        <p
          className="font-serif text-center py-1"
          style={{ fontSize: "1.05rem", color: "hsl(var(--foreground) / 0.45)" }}
        >
          {trackTitle}
        </p>
      ) : null}
    </div>
  );
}
